import { useEffect, useState } from 'react';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';

// ============================================================================
// CHA-592 / W2b — workflow-runs subscription (the Work list)
// ============================================================================
// The FIRST deliberate second live subscription in the shell (W1b was findings-only, one source of
// truth — CHA-239 / CHA-272). Runs are a DISTINCT collection and a distinct state machine from
// findings (runModel.js), so they get their own subscription rather than being folded into
// useAgentTickets. ONE onSnapshot on `tenants/{tenantId}/runs`, tenant-scoped, cleaned up on
// unmount. Read-only; every WRITE still goes through the auth-gated `yulieTicketAction` gateway
// (start_run) — Firestore rules deny direct client writes to runs (CHA-583).
//
// InMocean-only (Path 1); the tenant path is hardcoded like the detection runtime and useAgentTickets,
// generalization deferred to CHA-25.
const TENANT_ID = 'inmocean';

// Map a raw run doc to the Work-list item shape. `kind: 'run'` is what groupFindings/WorkList key on
// to keep a run out of the finding "Open / not sent" group (they share the literal state 'open').
// Only the fields the list renders — the run stays a thin link, not an evidence copy (evidenceRefs
// are POINTERS the gateway copied from the finding, not the evidence dump).
export const toRunItem = (r) => ({
  kind: 'run',
  id: r.id,
  state: r.state,
  definitionId: r.definitionId || null,
  workflowRef: r.workflowRef || null,
  findingId: r.findingId || null,
  po_no: r?.evidenceRefs?.po_no != null ? r.evidenceRefs.po_no : null,
  rule: r?.evidenceRefs?.rule || null,
  ownerUid: r.ownerUid || null,
  labels: Array.isArray(r.labels) ? r.labels : [],
});

// useRuns({ enabled }) — { runs, loading, error }. `runs` is already mapped to item shape (kind='run').
export default function useRuns({ enabled = true } = {}) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setRuns([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const db = getFirestore();
    let qRuns;
    try {
      qRuns = collection(db, `tenants/${TENANT_ID}/runs`);
    } catch (e) {
      console.error('shell: failed to build runs query', e);
      setError('Could not load workflow runs.');
      setLoading(false);
      return undefined;
    }

    // Same safety net as useAgentTickets — never hang on a silent Firestore stall; a late snapshot
    // clears the soft notice.
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError((prev) => prev || 'Still loading — Firestore is slow to respond.');
    }, 8000);

    const unsub = onSnapshot(
      qRuns,
      (snap) => {
        clearTimeout(timeoutId);
        setRuns(snap.docs.map((d) => toRunItem({ id: d.id, ...d.data() })));
        setError(null);
        setLoading(false);
      },
      (err) => {
        clearTimeout(timeoutId);
        console.error('shell: runs subscription failed', err);
        setError('Live workflow runs unavailable.');
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsub();
    };
  }, [enabled]);

  return { runs, loading, error };
}
